import "bootstrap/dist/css/bootstrap.min.css";
import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { addItem } from '../store/cartSlice.js';
import { fetchSpecialOffers } from '../store/specialOffersSlice.js';
import { fetchProducts } from '../store/productsSlice.js';
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const SpecialOffersCarousel = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const specialOffers = useSelector(s => s.specialOffers.items);
  const products = useSelector(s => s.products.items);
  const [currentSlide, setCurrentSlide] = useState(0);
  const rowRef = useRef(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeftRef = useRef(0);
  const [canLeft, setCanLeft] = useState(false); // eslint-disable-line no-unused-vars
  const [canRight, setCanRight] = useState(true); // eslint-disable-line no-unused-vars

  const themes = {
    "flash-sale":       { color: "#ff4757", light: "#fff0f1", icon: "⚡", gradient: "linear-gradient(135deg, #ff4757 0%, #c0392b 100%)", bg: "linear-gradient(135deg, #1a0005 0%, #3d0010 50%, #1a0005 100%)" },
    "weekend-deal":     { color: "#3742fa", light: "#f0f1ff", icon: "🎯", gradient: "linear-gradient(135deg, #3742fa 0%, #2f32e2 100%)", bg: "linear-gradient(135deg, #00001a 0%, #00003d 50%, #00001a 100%)" },
    "mega-offer":       { color: "#00b894", light: "#f0fff8", icon: "🔥", gradient: "linear-gradient(135deg, #00b894 0%, #00cec9 100%)", bg: "linear-gradient(135deg, #001a14 0%, #003d30 50%, #001a14 100%)" },
    "limited-time":     { color: "#e17055", light: "#fff3f0", icon: "⏰", gradient: "linear-gradient(135deg, #e17055 0%, #d63031 100%)", bg: "linear-gradient(135deg, #1a0800 0%, #3d1500 50%, #1a0800 100%)" },
    "special-discount": { color: "#a55eea", light: "#f8f0ff", icon: "💎", gradient: "linear-gradient(135deg, #a55eea 0%, #3742fa 100%)", bg: "linear-gradient(135deg, #0d001a 0%, #1e003d 50%, #0d001a 100%)" },
  };

  useEffect(() => {
    dispatch(fetchSpecialOffers());
    dispatch(fetchProducts());
  }, [dispatch]);

  useEffect(() => {
    if (specialOffers.length > 1) {
      const interval = setInterval(() => setCurrentSlide(prev => (prev + 1) % specialOffers.length), 6000);
      return () => clearInterval(interval);
    }
  }, [specialOffers.length]);

  useEffect(() => {
    if (rowRef.current) { rowRef.current.scrollLeft = 0; updateArrows(); }
  }, [currentSlide]);

  const updateArrows = () => {
    const el = rowRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 10);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 10);
  };

  useEffect(() => {
    const el = rowRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrows, { passive: true });
    updateArrows();
    return () => el.removeEventListener("scroll", updateArrows);
  }, [currentSlide, specialOffers]);

  const onMouseDown = (e) => {
    isDragging.current = true;
    startX.current = e.pageX - rowRef.current.offsetLeft;
    scrollLeftRef.current = rowRef.current.scrollLeft;
    rowRef.current.style.cursor = "grabbing";
  };
  const stopDrag = () => { isDragging.current = false; if (rowRef.current) rowRef.current.style.cursor = "grab"; };
  const onMouseMove = (e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    rowRef.current.scrollLeft = scrollLeftRef.current - (e.pageX - rowRef.current.offsetLeft - startX.current) * 1.5;
  };
  const scroll = (dir) => rowRef.current.scrollBy({ left: dir * 320, behavior: "smooth" });

  const calcPrice = (price, discount) => {
    const p = parseFloat(price) || 0, d = parseFloat(discount) || 0;
    return p <= 0 || d <= 0 ? p : Math.round(p - (p * d / 100));
  };

  const handleAddToCart = (product, offer, e) => {
    e?.stopPropagation();
    const price = calcPrice(product.price, offer.discount);
    dispatch(addItem({ ...product, originalPrice: product.price, price, specialOffer: { id: offer.id, title: offer.title, discount: offer.discount } }));
    toast.success(`${product.name} added with ${offer.discount}% off!`);
  };

  const handleBuyNow = (product, offer, e) => {
    e?.stopPropagation();
    handleAddToCart(product, offer, e);
    navigate('/cart');
  };

  if (specialOffers.length === 0) return null;

  const currentOffer = specialOffers[currentSlide];
  const offerProducts = products.filter(p => currentOffer.selectedProducts?.includes(p.id));
  const theme = themes[currentOffer.theme] || themes["flash-sale"];

  return (
    <div className="flash-wrapper" style={{ background: theme.bg }}>
      {/* Decorative blobs */}
      <div className="flash-blob flash-blob-1" style={{ background: theme.color }}></div>
      <div className="flash-blob flash-blob-2" style={{ background: theme.color }}></div>
      <div className="flash-blob flash-blob-3" style={{ background: theme.color }}></div>

      <div className="flash-inner">
        {/* Top urgency strip */}
        <div className="flash-urgency-strip" style={{ background: theme.gradient }}>
          <span className="flash-urgency-text">
            {theme.icon} {theme.icon} LIMITED TIME OFFER — GRAB IT BEFORE IT'S GONE! {theme.icon} {theme.icon}
          </span>
        </div>

        {/* Header */}
        <div className="flash-hero">
          <div className="flash-hero-left">
            <div className="flash-discount-stamp" style={{ background: theme.gradient }}>
              <span className="flash-stamp-num">{currentOffer.discount}%</span>
              <span className="flash-stamp-off">OFF</span>
            </div>
            <div>
              <h2 className="flash-main-title">{currentOffer.title}</h2>
              <p className="flash-main-sub">{currentOffer.subtitle || "Exclusive deals on premium fragrances · Limited stock"}</p>
            </div>
          </div>

          {/* Offer switcher tabs */}
          {specialOffers.length > 1 && (
            <div className="flash-tabs">
              {specialOffers.map((offer, i) => {
                const t = themes[offer.theme] || themes["flash-sale"];
                return (
                  <button
                    key={i}
                    className={`flash-tab${i === currentSlide ? " active" : ""}`}
                    style={i === currentSlide ? { background: t.gradient, color: "#fff", borderColor: "transparent" } : {}}
                    onClick={() => setCurrentSlide(i)}
                  >
                    {t.icon} {offer.title}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Scrollable product row */}
        <div className="cat-row-wrap flash-row-wrap">
          <button className="cat-float-btn cat-float-left flash-arrow" onClick={() => scroll(-1)}>
            <i className="bi bi-chevron-left"></i>
          </button>

          <div
            ref={rowRef}
            onMouseDown={onMouseDown}
            onMouseLeave={stopDrag}
            onMouseUp={stopDrag}
            onMouseMove={onMouseMove}
            className="cat-row category-row flash-product-row"
          >
            {offerProducts.length === 0 && (
              <p className="text-white opacity-50 ps-2 my-4">No products in this offer.</p>
            )}
            {offerProducts.map((product) => {
              const original = parseFloat(product.price) || 0;
              const discounted = calcPrice(original, currentOffer.discount);
              const savings = original - discounted;
              return (
                <div key={product.id} className="cat-card-wrap" onClick={() => navigate(`/product/${product.id}`)}>
                  <div className="cat-card flash-card">
                    <div className="cat-img-wrap">
                      <img
                        src={product.images?.[0] || product.image || "/slider/l1.jpeg"}
                        alt={product.name}
                        className="cat-img"
                        draggable={false}
                      />
                      <span className="cat-badge flash-pct-badge" style={{ background: theme.gradient }}>
                        {currentOffer.discount}% OFF
                      </span>
                      <div className="cat-overlay">
                        <button className="cat-overlay-btn" onClick={(e) => handleAddToCart(product, currentOffer, e)}>
                          <i className="bi bi-cart-plus-fill me-1"></i> Add to Cart
                        </button>
                        <button className="cat-overlay-buy" onClick={(e) => handleBuyNow(product, currentOffer, e)}>
                          <i className="bi bi-lightning-fill me-1"></i> Buy Now
                        </button>
                      </div>
                    </div>
                    <div className="cat-body flash-card-body">
                      <p className="cat-name">{product.name}</p>
                      <div className="cat-price-row">
                        <span className="cat-price" style={{ color: theme.color }}>₹{discounted.toLocaleString()}</span>
                        <span className="cat-old-price">₹{original.toLocaleString()}</span>
                      </div>
                      <p className="flash-save-tag" style={{ color: theme.color }}>
                        <i className="bi bi-tag-fill me-1"></i>Save ₹{savings.toLocaleString()}
                      </p>
                      <div className="cat-mobile-actions">
                        <button className="cat-mobile-cart" onClick={(e) => handleAddToCart(product, currentOffer, e)}>
                          <i className="bi bi-cart-plus-fill" style={{ color: "#fff" }}></i> Cart
                        </button>
                        <button className="cat-mobile-buy" onClick={(e) => handleBuyNow(product, currentOffer, e)}>
                          <i className="bi bi-lightning-fill"></i> Buy
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <button className="cat-float-btn cat-float-right flash-arrow" onClick={() => scroll(1)}>
            <i className="bi bi-chevron-right"></i>
          </button>
        </div>

        {/* Dot indicators */}
        {specialOffers.length > 1 && (
          <div className="flash-dots">
            {specialOffers.map((_, i) => (
              <button
                key={i}
                className={`flash-dot${i === currentSlide ? " active" : ""}`}
                style={i === currentSlide ? { background: theme.color, width: 28 } : {}}
                onClick={() => setCurrentSlide(i)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SpecialOffersCarousel;
